import * as PIXI from "pixi.js";
import cat1Image from "../assets/cat1.png";
import cat2Image from "../assets/cat2.png";
import { charaSize } from "../constants";
import { CollisionManager, createRect } from "../managers/collisionManager";
import { HeartManager } from "../managers/heartManager";
import { LoadManager } from "../managers/loadManager";
import { SoundManager } from "../managers/soundManager";
import { clamp } from "../utils/clamp";

export function loadCatAssets(loadManager: LoadManager) {
  return {
    cat1: loadManager.loadTexture(cat1Image),
    cat2: loadManager.loadTexture(cat2Image),
  } as const;
}

export type SetupCatProps = Readonly<{
  catAssets: ReturnType<typeof loadCatAssets>;
  parent: PIXI.Container;
  ticker: PIXI.Ticker;
  soundManager: SoundManager;
  heartManager: HeartManager;
  collisionManager: CollisionManager;
  startX: number;
  endX: number;
  y: number;
  intervalMs: number;
}>;

export function setupCat({
  catAssets,
  parent,
  ticker,
  collisionManager,
  soundManager,
  heartManager,
  startX,
  endX,
  y,
  intervalMs,
}: SetupCatProps) {
  const cat = new PIXI.Sprite(catAssets.cat1.get());
  cat.anchor.set(0.5, 0.7);
  cat.width = charaSize * 2;
  cat.height = charaSize * 2;
  cat.x = startX;
  cat.y = y;

  const delta = endX - startX;

  const maxVelocityX = (endX - startX) / intervalMs;
  let velocityX = maxVelocityX;

  const onTick = () => {
    const rect = createRect(
      "cat",
      cat.x - charaSize,
      cat.y - charaSize / 3,
      charaSize * 2,
      (charaSize / 3) * 2,
    );
    const collides = collisionManager.check(rect);
    if (collides.some((c) => c.type === "player") && heartManager.num > 0) {
      soundManager.playDamageSE();
      heartManager.robbed();
      parent.removeChild(cat);
      ticker.remove(onTick);
    }

    const animationState = Math.floor(performance.now() / 500) & 1;
    if (animationState) {
      cat.texture = catAssets.cat1.get();
    } else {
      cat.texture = catAssets.cat2.get();
    }

    cat.x += velocityX * ticker.deltaMS;
    cat.x =
      endX > startX ? clamp(cat.x, startX, endX) : clamp(cat.x, endX, startX);
    if (cat.x === startX) {
      velocityX = maxVelocityX;
    } else if (cat.x == endX) {
      velocityX = -maxVelocityX;
    }

    cat.scale.set(
      ((charaSize * 2) / cat.texture.width) * (velocityX > 0 ? -1 : 1),
      (charaSize * 2) / cat.texture.height,
    );
  };

  parent.addChild(cat);
  ticker.add(onTick);
  const dispose = () => {
    parent.removeChild(cat);
    ticker.remove(onTick);
  };

  return dispose;
}
